import React, { FC, useEffect, useState } from "react";
import { LIBRARY_DISBURSEMENTS_QUERY } from "../../../../graphql/query";
import { TaxLabelStatus } from "../../../../graphql/__generated_types__/globalTypes";
import { useMutation, useQuery } from "@apollo/client";
import {
  CREATE_LIBRARY_DISBURSEMENT,
  UPDATE_LIBRARY_DISBURSEMENTS,
} from "../../../../graphql/mutation";
import { Button, Select, Table, InputNumber, Form, Checkbox, Tooltip, Modal } from "antd";
import { InfoCircleOutlined, PlusOutlined, SaveOutlined } from "@ant-design/icons";
import { Icons, LibraryDisbursement, Spinner } from "@design-system";
import { useSettings } from "~/hooks";
import { compareAsc } from "date-fns";
import TextArea from "antd/lib/input/TextArea";
import produce from "immer";
import {
  CreateLibraryDisbursementMutationVariables,
  UpdateLibraryDisbursementsMutationVariables,
} from "~/gql/graphql";

type Update = {
  id: number;
  vatStatus: TaxLabelStatus;
  amount: number;
  name: string;
  changed: boolean;
  toDelete: boolean;
  updatedAt: Date;
  createdBy?: { id: number; userName: string; displayName: string } | null;
};

export const DisbursementsManager = () => {
  const { loading, data } = useQuery(LIBRARY_DISBURSEMENTS_QUERY, {
    variables: { filter: {} },
  });

  const libraryDisbursements = data?.libraryDisbursements || [];

  const [updateLibraryDisbursements, { loading: updateLoading }] = useMutation(
    UPDATE_LIBRARY_DISBURSEMENTS,
    { refetchQueries: [LIBRARY_DISBURSEMENTS_QUERY] }
  );

  const [createLibraryDisbursement, { loading: createLoading }] = useMutation(
    CREATE_LIBRARY_DISBURSEMENT,
    { refetchQueries: [LIBRARY_DISBURSEMENTS_QUERY] }
  );

  return (
    <DisbursementsTable
      createLibraryDisbursement={createLibraryDisbursement}
      updateLibraryDisbursements={updateLibraryDisbursements}
      updateLoading={updateLoading || createLoading}
      queryLoading={loading}
      libraryDisbursements={libraryDisbursements}
    />
  );
};

const DisbursementsTable = ({
  createLibraryDisbursement,
  updateLibraryDisbursements,
  updateLoading,
  queryLoading,
  libraryDisbursements,
}: {
  createLibraryDisbursement: (data: {
    variables: CreateLibraryDisbursementMutationVariables;
    onCompleted: () => void;
  }) => void;
  updateLibraryDisbursements: (data: {
    variables: UpdateLibraryDisbursementsMutationVariables;
    onCompleted: () => void;
  }) => void;
  updateLoading: boolean;
  queryLoading: boolean;
  libraryDisbursements: LibraryDisbursement[];
}) => {
  const { settings } = useSettings();
  const currencySymbol = settings?.currency.symbol ?? "";

  const initialState = libraryDisbursements?.reduce((acc, curr) => {
    if (!curr) return acc;
    acc[curr.id] = {
      id: curr.id,
      name: curr.name,
      vatStatus: curr.vatStatus,
      amount: curr.amount,
      changed: false,
      toDelete: false,
      updatedAt: curr.updatedAt,
      createdBy: curr.createdBy,
    };
    return acc;
  }, {} as Record<number, Update>);

  const [formState, setFormState] = useState<Record<string, Update>>(initialState);
  const [showAddNewModal, setShowAddNewModal] = useState(false);
  const [modalForm] = Form.useForm();

  useEffect(() => {
    setFormState(initialState);
  }, [libraryDisbursements]);

  const handleModalOk = () => {
    modalForm
      .validateFields()
      .then((values) => {
        createLibraryDisbursement({
          variables: {
            data: {
              amount: values.amount,
              name: values.name,
              vatStatus: values.vatStatus,
            },
          },
          onCompleted: () => setShowAddNewModal(false),
        });
      })
      .catch((err) => {
        console.log({ err });
      });
  };

  const onSubmit = () => {
    const changed = Object.values(formState).filter((x) => x.changed === true);

    if (changed.length === 0) return;

    updateLibraryDisbursements({
      variables: {
        data: changed.map((x) => ({
          id: x.id,
          name: x.name,
          vatStatus: x.vatStatus,
          amount: x.amount,
          delete: x.toDelete,
        })),
      },
      onCompleted: () => {},
    });
  };

  const loading = queryLoading || updateLoading;

  return (
    <div>
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            disabled={loading || showAddNewModal}
            onClick={() => setShowAddNewModal(true)}
          >
            Add New
          </Button>
          <Button
            size="large"
            disabled={Object.values(formState).every((x) => x.changed === false)}
            type="primary"
            icon={<SaveOutlined />}
            onClick={() => {
              onSubmit();
            }}
          >
            Save
          </Button>
        </div>
        <Table
          loading={loading}
          scroll={{ y: "calc(100vh - 350px)" }}
          columns={[
            {
              title: "Name",
              dataIndex: "name",
              key: "name",
              width: "60rem",
              render: (val, record) => {
                return (
                  <TextArea
                    value={val}
                    onChange={(e) => {
                      setFormState(
                        produce(formState, (draft) => {
                          draft[record.id].name = e.target.value;
                          draft[record.id].changed =
                            e.target.value !== initialState[record.id].name;
                        })
                      );
                    }}
                  />
                );
              },
            },
            {
              title: (
                <>
                  <span>VAT Status</span>
                  <VatStatusTooltip />
                </>
              ),
              dataIndex: "vatStatus",
              key: "vatStatus",
              width: "15rem",
              render: (val, record) => {
                return (
                  <Select
                    style={{ width: "100%" }}
                    value={val}
                    onChange={(value) => {
                      setFormState(
                        produce(formState, (draft) => {
                          draft[record.id].vatStatus = value;
                          draft[record.id].changed = value !== initialState[record.id].vatStatus;
                        })
                      );
                    }}
                    options={[
                      { value: TaxLabelStatus.Inclusive, label: "Inclusive" },
                      { value: TaxLabelStatus.Exempt, label: "Exempt" },
                      { value: TaxLabelStatus.Exclusive, label: "Exclusive" },
                    ]}
                  />
                );
              },
            },
            {
              title: "Amount",
              dataIndex: "amount",
              key: "amount",
              width: "15rem",
              render: (val, record) => {
                return (
                  <InputNumber
                    style={{ width: "100%" }}
                    value={val}
                    onChange={(value) => {
                      setFormState(
                        produce(formState, (draft) => {
                          draft[record.id].amount = value ?? 0;
                          draft[record.id].changed = value !== initialState[record.id].amount;
                        })
                      );
                    }}
                    min={0}
                    max={9999999}
                    prefix={currencySymbol}
                    step={0.01}
                  />
                );
              },
            },
            {
              title: "Last updated",
              dataIndex: "updatedAt",
              key: "updatedAt",
              render: (_, row) => {
                return new Date(row.updatedAt).toLocaleString();
              },
              sorter: (a, b) => compareAsc(new Date(a.updatedAt), new Date(b.updatedAt)),
              width: "15rem",
            },
            {
              title: "Created by",
              dataIndex: ["createdBy", "displayName"],
              key: "createdBy",
              width: "15rem",
            },
            {
              title: Icons.Delete,
              dataIndex: "delete",
              key: "delete",
              render: (_, row) => {
                return (
                  <Checkbox
                    checked={formState[row.id]?.toDelete ?? false}
                    onChange={(e) => {
                      const key = row.id;
                      const currState = formState[key];
                      setFormState((curr) => ({
                        ...curr,
                        [row.id]: {
                          ...currState,
                          toDelete: e.target.checked,
                          changed: e.target.checked,
                        },
                      }));
                    }}
                  />
                );
              },
            },
          ]}
          pagination={{
            onChange() {
              if (Object.values(formState).every((x) => x.changed === false)) return;
              onSubmit();
            },
            showSizeChanger: true,
            pageSizeOptions: [10, 25, 50, 100],
          }}
          dataSource={
            formState
              ? Object.values(formState).sort((a, b) => compareAsc(new Date(b.id), new Date(a.id)))
              : []
          }
          rowKey="id"
        />
      </div>

      <Modal
        title="Create New Disbursement"
        centered
        width={700}
        onOk={handleModalOk}
        onCancel={() => setShowAddNewModal(false)}
        open={showAddNewModal}
        destroyOnClose
        closable
      >
        <Form preserve={false} form={modalForm} labelCol={{ span: 4 }} wrapperCol={{ span: 20 }}>
          <Form.Item rules={[{ required: true }]} name="name" label="Name">
            <TextArea />
          </Form.Item>
          <Form.Item
            rules={[{ required: true }]}
            name="vatStatus"
            label="VAT Status"
            initialValue={TaxLabelStatus.Inclusive}
          >
            <Select
              options={[
                { value: TaxLabelStatus.Inclusive, label: "Inclusive" },
                { value: TaxLabelStatus.Exempt, label: "Exempt" },
                { value: TaxLabelStatus.Exclusive, label: "Exclusive" },
              ]}
            />
          </Form.Item>
          <Form.Item rules={[{ required: true }]} name="amount" label="Amount" initialValue={0}>
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              max={9999999}
              prefix={currencySymbol}
              step={0.01}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

const VatStatusTooltip: FC<{ children?: React.ReactNode }> = ({ children }) => (
  <Tooltip
    title={() => {
      return (
        <>
          <p>
            <strong>Inclusive:</strong> The amount includes VAT in its total.
          </p>
          <p>
            <strong>Exclusive:</strong> The amount does not include VAT. VAT will be added on the
            proposal.
          </p>
          <p>
            <strong>Exempt:</strong> The amount does not include VAT. VAT will not be added.
          </p>
        </>
      );
    }}
    placement="top"
  >
    {children}
    <InfoCircleOutlined style={{ marginLeft: 5 }} />
  </Tooltip>
);
